import { useState, useCallback, useMemo } from 'react';
import { defaultFilters, filtersSchemaByTab } from '../utils/statsFilterUtils';

/**
 * Unified Statistics Filters Hook
 * Manages draft and applied filter states with schema-driven visibility
 * 
 * @param {string} activeTab - Current active tab
 * @returns {Object} Filter state and handlers
 */
export function useStatsFilters(activeTab = 'overview') {
  // Applied filters - used for API requests
  const [appliedFilters, setAppliedFilters] = useState(() => {
    // Initialize with default filters + computed date range
    const initial = { ...defaultFilters };
    return initial;
  });

  // Draft filters - values user is editing (not yet applied)
  const [draftFilters, setDraftFilters] = useState(() => ({ ...defaultFilters }));

  // Get filter schema for current tab
  const filterSchema = useMemo(() => {
    return filtersSchemaByTab[activeTab] || filtersSchemaByTab.overview;
  }, [activeTab]);

  // Update draft filter value
  const updateDraftFilter = useCallback((key, value) => {
    setDraftFilters(prev => ({
      ...prev,
      [key]: value
    }));
  }, []);

  // Update multiple draft filters at once
  const updateDraftFilters = useCallback((updates) => {
    setDraftFilters(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // Apply draft filters (set applied = draft, triggers fetch)
  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...draftFilters });
  }, [draftFilters]);

  // Clear all filters (reset to defaults)
  const clearFilters = useCallback(() => {
    const cleared = { ...defaultFilters };
    setDraftFilters(cleared);
    setAppliedFilters(cleared);
  }, []);

  // Reset draft to match applied (cancel changes)
  const resetDraft = useCallback(() => {
    setDraftFilters({ ...appliedFilters });
  }, [appliedFilters]);

  // Sync draft with applied when tab changes (to show current filters)
  const syncDraftWithApplied = useCallback(() => {
    setDraftFilters({ ...appliedFilters });
  }, [appliedFilters]);

  // Get visible filter keys based on schema
  const visibleFilterKeys = useMemo(() => {
    return Object.keys(filterSchema).filter(key => filterSchema[key] === true);
  }, [filterSchema]);

  return {
    // State
    appliedFilters,
    draftFilters,
    filterSchema,
    visibleFilterKeys,
    
    // Actions
    updateDraftFilter,
    updateDraftFilters,
    applyFilters,
    clearFilters,
    resetDraft,
    syncDraftWithApplied,
    
    // Direct setter (for programmatic updates)
    setAppliedFilters
  };
}
